import React, { useEffect, useRef, FCC, KeyboardEvent, useMemo } from 'react'
import { useFormik } from 'formik'
import { isMac, useIsMountedRef, markdown, htmlDecode } from '@sketch/utils'
import * as yup from 'yup'

import { ReactComponent as AtIcon } from '@sketch/icons/at-email-24'
import { ReactComponent as EmojiIcon } from '@sketch/icons/emoji-face-24'
import {
  Tooltip,
  Breakpoint,
  Button,
  SupportsTouch,
  SuggestionsInput,
} from '@sketch/components'

import {
  Footer,
  Wrapper,
  IconButton,
  ActionsWrapper,
} from './CommentInput.styles'

import { ActiveEditingComment } from '../CommentEditContext'
import useMentionableUsers from '../../operations/useMentionableUsers'

export interface CommentInputProps {
  shareIdentifier: string
  className?: string
  autoFocus?: boolean
  onCommentSubmit: (comment: string) => void
  onCommentChange?: (comment: string) => void
  onCancel?: () => void
  isReply?: boolean
  isEditing?: boolean
  comment?: string
  banner?: React.ReactElement
  /**
   * Hides the mention button and skips the mentionable-users query, used in
   * contexts where @-mentions don't make sense (e.g. share preview).
   */
  disableMentions?: boolean
}

const commentSchema = yup.object().shape({
  comment: yup
    .string()
    .test(
      'empty-check',
      'Comments cannot be blank',
      value => typeof value === 'string' && value.trim().length > 0
    ),
})

export const shortcutSuggestionTooltipContent = () =>
  isMac() ? 'Command-Enter' : 'Control-Enter'

/**
 * Modal close animation is 300ms (see ModalTransition.tsx).
 * We add 50ms buffer to ensure focus happens after the modal's
 * focus trap cleanup runs on unmount.
 */
const MODAL_CLOSE_ANIMATION_DELAY = 350

export const CommentInput: FCC<CommentInputProps> = props => {
  const {
    autoFocus,
    shareIdentifier,
    className,
    onCommentSubmit,
    onCommentChange,
    onCancel,
    isReply,
    isEditing,
    comment = '',
    banner,
    disableMentions = false,
  } = props

  const isComponentMounted = useIsMountedRef()
  // Comments are coming HTML encoded from the API side.
  const decodedComment = useMemo(() => htmlDecode(comment), [comment])

  const { values, setValues, handleSubmit, dirty, isValid, submitForm } =
    useFormik({
      initialValues: { comment: decodedComment },
      validationSchema: commentSchema,
      onSubmit: async ({ comment }, { resetForm }) => {
        if (!comment) return
        onCommentSubmit?.(comment.trim())

        // Prevent state from changing if the component is already unmounted
        isComponentMounted.current && resetForm()
      },
    })

  // Focus on the comment input when thread panel loads
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const input = inputRef.current

    if (autoFocus) {
      if (!input) {
        return
      }

      // Delay focus to allow modal close animation to complete,
      // otherwise the modal's focus trap cleanup may steal focus back
      setTimeout(() => {
        input.focus()

        // Force the caret to be on the end of the input and scroll to bottom
        const textLength = input.value?.length || 0
        input.selectionStart = textLength
        input.selectionEnd = textLength
        input.scrollTop = input.scrollHeight
      }, MODAL_CLOSE_ANIMATION_DELAY)

      return () => {
        if (document.activeElement === input) {
          input.blur()
        }
      }
    }
  }, [autoFocus])

  useEffect(() => {
    setValues({ comment: decodedComment })
  }, [decodedComment, setValues])

  const handleSuggestionsKeyDown = (event: KeyboardEvent) => {
    const auxKey = event.metaKey || event.ctrlKey

    /* Check if submit key command */
    if (auxKey && event.key === 'Enter') {
      /* Submit the form so the comment can be created */
      submitForm()
      return
    }

    const markDownChange = markdown.getChangeByEventKey(event.key)
    if (auxKey && markDownChange) {
      event.preventDefault()

      if (inputRef.current) {
        const { selectionStart, selectionEnd } = inputRef.current

        const { text, ...selection } = markdown.toggleTextWithChange(
          inputRef.current.value,
          markDownChange,
          selectionStart,
          selectionEnd
        )

        setValues({ comment: text }).then(() => {
          /* Force the input selection after the text change  */
          inputRef?.current?.setSelectionRange(
            selection.selectionStart,
            selection.selectionEnd
          )
        })
      }
      return
    }
  }

  const insertEmojiTrigger = () => {
    // Append trigger character (with space if there's existing text)
    const newValue = values.comment.length > 0 ? `${values.comment} :` : ':'
    setValues({ comment: newValue })

    // Focus and move caret to end after value updates
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus()
        const len = newValue.length
        inputRef.current.setSelectionRange(len, len)
      }
    }, 0)
  }

  const insertMentionTrigger = () => {
    // Append trigger character (with space if there's existing text)
    const newValue = values.comment.length > 0 ? `${values.comment} @` : '@'
    setValues({ comment: newValue })

    // Focus and move caret to end after value updates
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus()
        const len = newValue.length
        inputRef.current.setSelectionRange(len, len)
      }
    }, 0)
  }

  const [queryUsers, { loading, users, called }] =
    useMentionableUsers(shareIdentifier)

  // Only check for @ trigger, not the entire values object
  const lastChar = values.comment.charAt(values.comment.length - 1)

  useEffect(() => {
    if (!disableMentions && !called && lastChar === '@') {
      queryUsers()
    }
  }, [queryUsers, called, lastChar, disableMentions])

  // Memoize to prevent new array reference on every render
  const mentionableUsers = useMemo(
    () =>
      disableMentions
        ? []
        : users.map(user => ({
            id: user.identifier,
            display: user.name || undefined,
            avatar: user.avatar?.small || '',
          })),
    [disableMentions, users]
  )

  const allowSubmit = (dirty || comment) && isValid

  return (
    <Wrapper ref={wrapperRef} className={className} data-testid="comment-input">
      {/**
       * "ActiveEditingComment" will warn the parent context (if exists) that
       * there's a comment being edited, if the user tries to edit another comment
       * a modal should show and prompt the user to choose between one or the other
       *
       * See "modules/annotations/components/CommentEditContext/CommentEditContext.tsx"
       * for more context
       */}
      {isEditing && (
        <ActiveEditingComment
          value={values.comment}
          isDirty={dirty}
          onCloseEditMode={() => {
            // In case we need to close this component
            // we should "submit" the original text
            onCommentSubmit(comment)
          }}
          onFocusComment={() => {
            // Wait for modal close animation to complete before focusing,
            // otherwise the focus trap cleanup will steal focus back
            setTimeout(() => {
              inputRef.current?.focus()
            }, MODAL_CLOSE_ANIMATION_DELAY)
          }}
        />
      )}
      {banner}
      <form onSubmit={handleSubmit}>
        <SuggestionsInput
          name="comment"
          inputRef={inputRef}
          value={values.comment}
          mentionableUsers={mentionableUsers}
          onChange={event => {
            setValues({ comment: event.target.value })
            onCommentChange?.(event.target.value)
          }}
          onKeyDown={handleSuggestionsKeyDown}
          placeholder={isReply ? 'Write a reply...' : 'Write a comment...'}
        />

        {/*
          We shouldn't show the emoji icon in mobile, since most phone keyboards
          have emoji support build in.

          https://github.com/sketch-hq/Cloud/issues/2004#issuecomment-539848866
        */}
        <Footer>
          <Breakpoint on="sm">
            <IconButton
              type="button"
              size="32"
              onClick={insertEmojiTrigger}
              data-testid="emoji-icon"
              variant="secondary"
            >
              <EmojiIcon role="img" />
              <span className="sr-only">Show Emoji selector</span>
            </IconButton>
          </Breakpoint>

          {!disableMentions && (
            <IconButton
              type="button"
              size="32"
              loading={loading}
              onClick={insertMentionTrigger}
              data-testid="mention-icon"
              variant="secondary"
            >
              <AtIcon role="img" />
              <span className="sr-only">Show Mentions selector</span>
            </IconButton>
          )}

          <ActionsWrapper>
            {isEditing && (
              <Button type="submit" size="32" onClick={onCancel}>
                Cancel
              </Button>
            )}

            <SupportsTouch>
              {supportsTouch => (
                <Tooltip
                  content={shortcutSuggestionTooltipContent()}
                  placement="top"
                  disabled={supportsTouch || !allowSubmit}
                >
                  {isEditing ? (
                    <Button
                      type="submit"
                      size="32"
                      disabled={!allowSubmit}
                      variant="primary"
                    >
                      Save
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      type="submit"
                      size="32"
                      disabled={!allowSubmit}
                    >
                      Send
                    </Button>
                  )}
                </Tooltip>
              )}
            </SupportsTouch>
          </ActionsWrapper>
        </Footer>
      </form>
    </Wrapper>
  )
}

export default CommentInput
